// Loads Cloudflare Turnstile's script once and exposes the minimal bits of
// its global API this app uses. Opt-in via VITE_TURNSTILE_SITE_KEY (see
// .env.example) - unset means TurnstileWidget renders nothing and no
// script is ever loaded, matching the backend's VerifyTurnstile no-op.

export interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

interface TurnstileGlobal {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
let scriptPromise: Promise<TurnstileGlobal> | null = null;

export function loadTurnstile(): Promise<TurnstileGlobal> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else {
        // Script tag loaded but Cloudflare's global never showed up (seen
        // with some content blockers that let the request through but
        // neuter the payload) - clear the cached promise so a later retry
        // actually re-attempts the load instead of replaying this same
        // rejection forever.
        scriptPromise = null;
        reject(new Error('Turnstile script loaded but window.turnstile is missing'));
      }
    };
    script.onerror = () => {
      // Blocked by an ad/privacy blocker or unreachable (offline, firewall)
      // - same reasoning as above: don't let one failed attempt poison
      // every future retry for the lifetime of the page.
      scriptPromise = null;
      reject(new Error('Failed to load the Turnstile script'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}
